import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { apiRequest } from '@/utils/api-request';
import { Link2, Loader2, Check, Mic } from 'lucide-react';

const API_BETA_URL = 'https://api.yellowslate.com';

const CITY_MAP: Record<number, string> = {
  0: 'All Cities',
  1: 'Hyderabad',
  2: 'Pune',
  5: 'Mumbai',
  6: 'Kolkata',
  8: 'Delhi',
  13: 'Bengaluru',
};


interface DbVideo {
  video_id: number;
  youtube_id: string;
  title: string;
  city_id_fk: number;
  podcast: number;
  publishedAt: string;
}

interface School {
  slate_id: number;
  slate_name: string;
  city_name: string;
  area_name: string;
}

interface LinkedSchoolInfo {
  slate_id: number;
  name: string;
  area: string;
  slug: string;
}

const LinkToSchools: React.FC = () => {
  const [videos, setVideos] = useState<DbVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState('all');
  const [linkedFilter, setLinkedFilter] = useState<'all' | 'not-linked' | 'linked'>('not-linked');

  // Per-row state
  const [searchTerms, setSearchTerms] = useState<Record<number, string>>({});
  const [searchResults, setSearchResults] = useState<Record<number, School[]>>({});
  const [searchLoading, setSearchLoading] = useState<Record<number, boolean>>({});
  // Multi-select: array of selected schools per video
  const [selectedSchools, setSelectedSchools] = useState<Record<number, School[]>>({});
  const [linkLoading, setLinkLoading] = useState<Record<number, boolean>>({});
  // All linked youtube_ids from DB — array of schools per youtube_id
  const [linkedMap2, setLinkedMap2] = useState<Record<string, LinkedSchoolInfo[]>>({});
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  // Fetch videos + linked IDs from DB in parallel on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [videosRes, linkedRes] = await Promise.all([
          fetch(`${API_BETA_URL}/v1/videos?limit=400&page=1`),
          apiRequest<{ youtube_id: string; slate_id: number; name: string; area: string; slug: string }[]>('/youtube-videos/linked-youtube-ids', { method: 'GET' }),
        ]);
        const videosData = await videosRes.json();
        setVideos(videosData.data?.data || []);
        if (linkedRes.success && linkedRes.data) {
          const map: Record<string, LinkedSchoolInfo[]> = {};
          linkedRes.data.forEach(r => {
            if (!map[r.youtube_id]) map[r.youtube_id] = [];
            map[r.youtube_id].push({ slate_id: r.slate_id, name: r.name, area: r.area, slug: r.slug });
          });
          setLinkedMap2(map);
        }
      } catch {
        toast.error('Failed to fetch videos');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const cities = [...new Set(videos.map(v => CITY_MAP[v.city_id_fk]).filter(Boolean))].sort();

  const linkedCount = videos.filter(v => (linkedMap2[v.youtube_id]?.length ?? 0) > 0).length;

  const filteredVideos = videos.filter(v => {
    const cityName = CITY_MAP[v.city_id_fk] || '';
    const matchesCity = cityFilter === 'all' || cityName === cityFilter;
    const isLinked = (linkedMap2[v.youtube_id]?.length ?? 0) > 0;
    const matchesLinked =
      linkedFilter === 'all' ||
      (linkedFilter === 'linked' && isLinked) ||
      (linkedFilter === 'not-linked' && !isLinked);
    return matchesCity && matchesLinked;
  });

  // Debounced school search
  const handleSearchChange = (videoId: number, term: string) => {
    setSearchTerms(prev => ({ ...prev, [videoId]: term }));

    if (debounceTimers.current[videoId]) clearTimeout(debounceTimers.current[videoId]);

    if (term.length < 2) {
      setSearchResults(prev => ({ ...prev, [videoId]: [] }));
      setOpenDropdown(null);
      return;
    }

    debounceTimers.current[videoId] = setTimeout(async () => {
      setSearchLoading(prev => ({ ...prev, [videoId]: true }));
      try {
        const res = await apiRequest<{ schools: School[] }>('/schools/search', {
          method: 'POST',
          body: { search_term: term, limit: 50 },
        });
        if (res.success) {
          setSearchResults(prev => ({ ...prev, [videoId]: res.data?.schools || [] }));
          setOpenDropdown(videoId);
        }
      } catch {
        // silent
      } finally {
        setSearchLoading(prev => ({ ...prev, [videoId]: false }));
      }
    }, 300);
  };

  // Toggle a school in/out of the selection for a video
  const handleToggleSchool = (videoId: number, school: School) => {
    setSelectedSchools(prev => {
      const current = prev[videoId] || [];
      const exists = current.find(s => s.slate_id === school.slate_id);
      if (exists) {
        return { ...prev, [videoId]: current.filter(s => s.slate_id !== school.slate_id) };
      } else {
        return { ...prev, [videoId]: [...current, school] };
      }
    });
    // Dropdown stays open (e.preventDefault on onMouseDown handles this)
  };

  const handleLink = async (video: DbVideo) => {
    const schools = selectedSchools[video.video_id] || [];
    if (schools.length === 0) return;

    setLinkLoading(prev => ({ ...prev, [video.video_id]: true }));
    try {
      const embedUrl = `https://www.youtube.com/embed/${video.youtube_id}`;
      const results = await Promise.all(
        schools.map(school =>
          apiRequest(`/media/${school.slate_id}/gallery/video`, {
            method: 'POST',
            body: { url: embedUrl },
          })
        )
      );

      const successfulSchools = schools.filter((_, i) =>
        results[i].success || results[i].message?.toLowerCase().includes('already linked')
      );
      const failedCount = schools.length - successfulSchools.length;

      if (successfulSchools.length > 0) {
        setLinkedMap2(prev => {
          const updated = { ...prev };
          const existing = updated[video.youtube_id] || [];
          const newEntries = successfulSchools
            .filter(s => !existing.find(e => e.slate_id === s.slate_id))
            .map(s => ({ slate_id: s.slate_id, name: s.slate_name, area: s.area_name || '', slug: '' }));
          updated[video.youtube_id] = [...existing, ...newEntries];
          return updated;
        });
        setSelectedSchools(prev => ({ ...prev, [video.video_id]: [] }));
        setSearchTerms(prev => ({ ...prev, [video.video_id]: '' }));
        setSearchResults(prev => ({ ...prev, [video.video_id]: [] }));
        setOpenDropdown(null);
        toast.success(`Linked to ${successfulSchools.length} school${successfulSchools.length > 1 ? 's' : ''}`);
      }

      if (failedCount > 0) {
        toast.error(`Failed to link ${failedCount} school${failedCount > 1 ? 's' : ''}`);
      }
    } catch {
      toast.error('Failed to link video');
    } finally {
      setLinkLoading(prev => ({ ...prev, [video.video_id]: false }));
    }
  };

  const thumbUrl = (youtubeId: string) =>
    `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Filters bar */}
      <div className="bg-white border-b border-gray-200 px-8 py-3 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">City:</span>
          <select
            value={cityFilter}
            onChange={e => setCityFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-brandColor focus:border-brandColor"
          >
            <option value="all">All Cities</option>
            {cities.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Status:</span>
          <select
            value={linkedFilter}
            onChange={e => setLinkedFilter(e.target.value as 'all' | 'not-linked' | 'linked')}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-brandColor focus:border-brandColor"
          >
            <option value="not-linked">Not Linked</option>
            <option value="linked">Linked</option>
            <option value="all">All</option>
          </select>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm font-medium text-green-700 bg-green-50 px-3 py-1 rounded-full">
            {linkedCount} of {videos.length} linked
          </span>
          <span className="text-sm text-gray-400">
            Showing {filteredVideos.length}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-brandColor" />
            <p className="text-sm text-gray-500">Loading videos...</p>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Thumb</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">City</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80">Link to School</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredVideos.map(video => {
                const linkedSchools = linkedMap2[video.youtube_id] || [];
                const isLinked = linkedSchools.length > 0;
                const isLinking = linkLoading[video.video_id];
                const selected = selectedSchools[video.video_id] || [];
                const results = searchResults[video.video_id] || [];
                const isSearching = searchLoading[video.video_id];
                const showDropdown = openDropdown === video.video_id && results.length > 0;

                return (
                  <tr
                    key={video.video_id}
                    className={`hover:bg-gray-50 transition-colors ${isLinked ? 'bg-green-50/40' : ''}`}
                  >
                    <td className="px-4 py-2.5">
                      <img src={thumbUrl(video.youtube_id)} alt="" className="w-16 h-9 object-cover rounded" />
                    </td>

                    <td className="px-4 py-2.5 max-w-xs">
                      <p className="text-gray-900 text-sm line-clamp-2 leading-snug" title={video.title}>
                        {video.title}
                      </p>
                      {video.podcast === 1 && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
                          <Mic className="h-2.5 w-2.5" />
                          Podcast
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-2.5">
                      <span className="text-xs text-gray-500">{CITY_MAP[video.city_id_fk] || '—'}</span>
                    </td>

                    <td className="px-4 py-2.5">
                      {isLinked ? (
                        <span className="inline-flex items-start gap-1.5 text-sm text-green-700 font-medium">
                          <Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                          <span className="flex flex-col gap-0.5">
                            {linkedSchools.map(s => (
                              <span key={s.slate_id}>
                                {s.slug ? (
                                  <a
                                    href={`https://www.yellowslate.com/school/${s.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                  >
                                    {s.name}
                                    {s.area && <span className="text-green-500 font-normal"> · {s.area}</span>}
                                  </a>
                                ) : (
                                  <span>
                                    {s.name}
                                    {s.area && <span className="text-green-500 font-normal"> · {s.area}</span>}
                                  </span>
                                )}
                              </span>
                            ))}
                          </span>
                        </span>
                      ) : (
                        // Not linked — show search + multi-select + link
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <input
                              type="text"
                              value={searchTerms[video.video_id] || ''}
                              onChange={e => handleSearchChange(video.video_id, e.target.value)}
                              onFocus={() => {
                                if ((searchResults[video.video_id] || []).length > 0)
                                  setOpenDropdown(video.video_id);
                              }}
                              onBlur={() => setTimeout(() => setOpenDropdown(null), 200)}
                              placeholder={selected.length > 0 ? `${selected.length} selected — search more` : 'Search school...'}
                              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-brandColor focus:border-brandColor pr-7"
                            />
                            {isSearching && (
                              <Loader2 className="absolute right-2 top-2 h-3.5 w-3.5 animate-spin text-gray-400" />
                            )}
                            {/* Selected schools chips */}
                            {selected.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {selected.map(s => (
                                  <span
                                    key={s.slate_id}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-brandColor/10 text-gray-800 border border-brandColor/30"
                                  >
                                    {s.slate_name}
                                    <button
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleToggleSchool(video.video_id, s);
                                      }}
                                      className="text-gray-500 hover:text-red-500 leading-none"
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                            {showDropdown && (
                              <div className="absolute z-30 left-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                                {results.map(s => {
                                  const isChecked = selected.some(sel => sel.slate_id === s.slate_id);
                                  return (
                                    <button
                                      key={s.slate_id}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleToggleSchool(video.video_id, s);
                                      }}
                                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center gap-2.5"
                                    >
                                      <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${isChecked ? 'bg-brandColor border-brandColor' : 'border-gray-300'}`}>
                                        {isChecked && <Check className="h-2.5 w-2.5 text-white" />}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{s.slate_name}</p>
                                        <p className="text-xs text-gray-500">
                                          {s.city_name}{s.area_name ? ` · ${s.area_name}` : ''}
                                        </p>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleLink(video)}
                            disabled={selected.length === 0 || isLinking}
                            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-brandColor text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-opacity-90 transition-opacity"
                          >
                            {isLinking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                            Link{selected.length > 1 ? ` (${selected.length})` : ''}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && filteredVideos.length === 0 && (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
            No videos match your filters
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkToSchools;
